import { SagaIterator } from "redux-saga";
import { call, put, select, takeLatest } from "typed-redux-saga";

import { Activity, ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { LoggerFactory } from "@com.mgmtp.a12.utils/utils-logging";

import { addDataHolder, createCustomNotificationAction } from "../../../app/actions";
import { RESOURCE_KEYS } from "../../../localization";
import { isWorkflowActivityWithLoadedData } from "../../../utilities/activityUtils";
import { hasRole } from "../../../utilities/rpcHelper";
import { isGatewayTimeoutError } from "../../../utilities/restHelper";
import { requestEditableAction } from "../../processFormEngine/sagas/actions";

import { retryTaskPermission, TASK_DETAIL_ACTIVITY_ID } from "../store/actions";
import { PERMISSON_DATAHOLDER } from "../store/constants";

const logger = LoggerFactory.getLogger("permission-saga");

export function* setPermissionInTask(isPermitted: boolean, activityId: string): SagaIterator<void> {
	const activity = yield* select(ActivitySelectors.activityById(activityId));
	if (!activity || !isWorkflowActivityWithLoadedData(activity)) {
		return;
	}

	if (!activity.dataHolders?.some(it => PERMISSON_DATAHOLDER in it.descriptor)) {
		try {
			const permissionDataholder: Activity.DataHolder = {
				descriptor: { [PERMISSON_DATAHOLDER]: PERMISSON_DATAHOLDER },
				dirty: false,
				savingState: "saved",
				loadingState: "missing",
				slices: {}
			};

			yield* put(addDataHolder({ dataHolder: permissionDataholder, activityId: activity.id }));
		} catch (e) {
			logger.log(e);
		}
	}

	try {
		yield* put(
			ActivityActions.setDataByDescriptor({
				descriptor: { [PERMISSON_DATAHOLDER]: PERMISSON_DATAHOLDER },
				activityId: activity.id,
				data: {
					isUserPermittedForTask: isPermitted
				}
			})
		);
	} catch (e) {
		logger.log(e);
	}
	yield* put(requestEditableAction({ hasTaskRole: isPermitted }));
}

export function* retryTaskPermissionSaga(): SagaIterator<void> {
	yield* takeLatest(retryTaskPermission, function* handleRetryTaskPermission(): SagaIterator<void> {
		const activity = yield* select(ActivitySelectors.activityById(TASK_DETAIL_ACTIVITY_ID));
		const instance = activity?.descriptor.instance;
		if (!instance) {
			return;
		}

		try {
			const isPermitted = yield* call(hasRole, instance);
			yield* call(setPermissionInTask, isPermitted, TASK_DETAIL_ACTIVITY_ID);
		} catch (error) {
			yield* call(putTaskPermissionTimeoutNotification, error);
		}
	});
}

export function* putTaskPermissionTimeoutNotification(error: unknown): SagaIterator<void> {
	logger.error(error);
	yield* put(
		createCustomNotificationAction({
			title: { key: RESOURCE_KEYS.features.notification.title.error },
			message: {
				key: isGatewayTimeoutError(error)
					? RESOURCE_KEYS.wf.notification.error.gatewayTimeout
					: RESOURCE_KEYS.wf.notification.error.taskPermission
			},
			icon: "error",
			severity: "error",
			componentId: "NotificationWithButton",
			activityId: TASK_DETAIL_ACTIVITY_ID,
			group: "task-permission-timeout",
			button: {
				action: retryTaskPermission(undefined),
				label: { key: RESOURCE_KEYS.wf.notification.error.gatewayTimeoutRetry }
			}
		})
	);
}
